/**
 * Authentication service for the SMC app (main ui).
 * Uses the main BFF API (same origin as app when deployed).
 */


export interface UserInfo {
    email: string;
    username: string;
    sub: string;
    customerUrl: string;
    customerId: number | null;
    customerType: string;
    sessionId: string;
    expiresAt: number | null;
}

export interface LoginResult {
    success: boolean;
    user?: Partial<UserInfo>;
    error?: string;
}

const PS_SESSION_TOKEN_KEY = 'ps_sessiontoken';

/**
 * Check if running on localhost
 */
function isLocalhost(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    const hostname = window.location.hostname;
    return (
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname === '[::1]'
    );
}

/**
 * Read a cookie value by name from document.cookie.
 * Returns undefined if the cookie is not found or not accessible (HttpOnly).
 */
function getCookieValue(name: string): string | undefined {
    if (typeof document === 'undefined') return undefined;
    const match = document.cookie
        .split(';')
        .map((c) => c.trim())
        .find((c) => c.startsWith(`${name}=`));
    if (!match) return undefined;
    const value = match.substring(name.length + 1);
    return value || undefined;
}

/**
 * Capture PS sessiontoken on app boot.
 *
 * Sources (checked in order):
 *  1. URL query parameter `?sessiontoken=…`  — used in dev when navigating
 *     from PS groot; the token is immediately removed from the URL.
 *  2. Existing `sessiontoken` cookie readable by JS — present when the
 *     cookie was set on a domain shared with SMC (e.g. `.powerschoolcorp.com`)
 *     or was previously captured and written as a same-domain cookie.
 *
 * When a token is found it is persisted to sessionStorage so subsequent
 * API requests can include it via the X-Session-Token header, and also
 * written as a same-domain cookie so the browser sends it automatically
 * to the BFF API on the `api.` subdomain.
 */
export function capturePsSessionToken(): void {
    if (typeof window === 'undefined') return;

    // 1. Check for token in URL query string
    const params = new URLSearchParams(window.location.search);
    const queryToken = params.get('sessiontoken');
    if (queryToken) {
        sessionStorage.setItem(PS_SESSION_TOKEN_KEY, queryToken);
        // Set a same-domain cookie so the browser sends it automatically.
        // SameSite=None + Secure is required because the API is on a
        // different subdomain (api.{env}.smc… vs {env}.smc…).
        // Include Domain so subdomains (e.g. api.dev.smc.…) also receive it.
        const hostname = window.location.hostname;
        const domainAttr = hostname.includes('.')
            ? `; Domain=.${hostname}`
            : '';
        document.cookie = `sessiontoken=${queryToken}; Path=/; Secure; SameSite=None${domainAttr}`;
        // Clean the token from the URL to avoid leaking via Referer / history
        params.delete('sessiontoken');
        const qs = params.toString();
        const cleanUrl =
            window.location.pathname +
            (qs ? `?${qs}` : '') +
            window.location.hash;
        window.history.replaceState(null, '', cleanUrl);
        return;
    }

    // 2. Check for an existing sessiontoken cookie (shared-domain scenario)
    if (!sessionStorage.getItem(PS_SESSION_TOKEN_KEY)) {
        const cookieToken = getCookieValue('sessiontoken');
        if (cookieToken) {
            sessionStorage.setItem(PS_SESSION_TOKEN_KEY, cookieToken);
            // Re-set as a same-domain cookie with Domain attribute so
            // the API subdomain (api.{env}.smc.…) also receives it.
            const hostname = window.location.hostname;
            const domainAttr = hostname.includes('.')
                ? `; Domain=.${hostname}`
                : '';
            document.cookie = `sessiontoken=${cookieToken}; Path=/; Secure; SameSite=None${domainAttr}`;
        }
    }
}

/**
 * Get the stored PS session token (if any).
 */
export function getPsSessionToken(): string | null {
    if (typeof window === 'undefined') return null;
    return sessionStorage.getItem(PS_SESSION_TOKEN_KEY) || null;
}

/**
 * Get the auth BFF URL (main API).
 * In production: app is at {env}.smc.mfe.powerschoolcorp.com, BFF at api.{env}.smc.mfe.powerschoolcorp.com
 * In local dev: use configured BFF URL with X-Session-Ref header
 */
function getAuthBffUrl(): string {
    const { origin, hostname } = window.location;

    // Main app is at {env}.smc.mfe.powerschoolcorp.com; BFF is at api.{env}.smc.mfe.powerschoolcorp.com
    const match = hostname.match(/^([^.]+)\.smc\.mfe\.powerschoolcorp\.com$/);
    if (match && match[1]) {
        return `https://api.${match[1]}.smc.mfe.powerschoolcorp.com`;
    }

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return `${origin}/api`;
    }

    console.warn(
        'Auth BFF URL could not be determined from hostname. Using origin.'
    );
    return origin;
}

export class AuthService {
    private static bffUrl = getAuthBffUrl();

    /**
     * Check if user is authenticated by calling GET /user
     */
    static async getCurrentUser(): Promise<UserInfo | null> {
        if (isLocalhost()) {
            const sessionToken = localStorage.getItem('sessionId');
            if (!sessionToken) {
                console.log(
                    '[AuthService] Running on localhost without session token - skipping auth check'
                );
                return null;
            }
        }

        try {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                // Match the shared API client so API Gateway invokes the
                // Lambda authorizer even when auth ultimately comes from
                // Cookie or X-Session-Token.
                Authorization: 'SessionContext'
            };
            // Include PS session token via header when cookie-based sharing
            // is not available (e.g., dev environments with domain mismatch)
            const psToken = getPsSessionToken();
            if (psToken) {
                headers['X-Session-Token'] = psToken;
            }

            const response = await fetch(`${this.bffUrl}/user`, {
                method: 'GET',
                headers,
                credentials: isLocalhost() ? 'omit' : 'include'
            });

            if (response.status === 403 || response.status === 401) {
                return null;
            }

            if (!response.ok) {
                throw new Error(`Failed to get user: ${response.status}`);
            }

            const data = await response.json();
            // Use the actual API response when it looks like user info (has sub or sessionId).
            // Return null only when unauthenticated: body is null or not a user-shaped object.
            // 403/401 above usually mean the session cookie was missing (e.g. Set-Cookie was not applied after login).
            if (data == null || typeof data !== 'object') {
                return null;
            }
            if (!('sub' in data) && !('sessionId' in data)) {
                return null;
            }
            return data as UserInfo;

        } catch (error) {
            console.error('Error getting current user:', error);
            return null;
        }
    }

    /**
     * Initiate login by redirecting to /auth/login
     */
    static login(customerUrl: string, redirectAfterLogin?: string): void {
        const params = new URLSearchParams({
            customerUrl
        });
        if (redirectAfterLogin) {
            params.set('redirect', redirectAfterLogin);
        }
        window.location.href = `${this.bffUrl}/auth/login?${params.toString()}`;
    }

    /**
     * Logout user: POST /api/auth/logout to clear session, then redirect to signin.
     * Uses the existing BFF POST endpoint so the user never lands on the API URL.
     */
    static logout(redirectAfterLogout?: string): void {
        const signInUrl =
            redirectAfterLogout ||
            `${typeof window !== 'undefined' ? window.location.origin : ''}/default/signin`;

        const doRedirect = (): void => {
            window.location.href = signInUrl;
        };

        fetch(`${this.bffUrl}/api/auth/logout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include'
        })
            .then(doRedirect)
            .catch(() => {
                // Redirect even if logout request fails (e.g. network)
                doRedirect();
            });
    }

    /**
     * Sign in via password grant (POST /auth/signin)
     */
    static async signIn(
        username: string,
        password: string,
        customerName: string
    ): Promise<LoginResult> {
        try {
            const useHeaderAuth = isLocalhost();
            // Use credentials: 'include' so the browser accepts and stores the Set-Cookie
            // from the signin response; otherwise the cookie is never set and /user gets 401.
            const response = await fetch(`${this.bffUrl}/auth/signin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    username,
                    password,
                    customerUrl: customerName
                })
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    error: data.error || 'Sign in failed'
                };
            }

            if (useHeaderAuth && data.sessionId) {
                localStorage.setItem('sessionId', data.sessionId);
            }

            return {
                success: true,
                user: data.claims as Partial<UserInfo>
            };
        } catch (error) {
            console.error('Error during sign in:', error);
            return {
                success: false,
                error:
                    error instanceof Error
                        ? error.message
                        : 'An unexpected error occurred'
            };
        }
    }

    /**
     * Get customerUrl from URL query parameter
     */
    static getCustomerUrlFromQuery(): string | null {
        const params = new URLSearchParams(window.location.search);
        return params.get('customerUrl');
    }

    /**
     * Get error message from URL query parameter
     */
    static getErrorFromQuery(): string | null {
        const params = new URLSearchParams(window.location.search);
        return params.get('error');
    }
}
